<template>
  <SwapPreview
    v-if="view === 'root'"
    :set-view="setView"
    :view="view"
    @close="onClose"
    @confirm="onSwapConfirm"
  >
  </SwapPreview>
  <SendTransaction
    v-if="view === 'tx'"
    :frontrun="isFrontrun"
    :payload="payload"
    :stable="isCurveStable"
    :title="$t('swap.swapTokens')"
    flow="swap"
    @back="onBack"
    @close="onClose"
    @reject="onRejected"
    @success="onSuccess"
  >
    <template #tx-params>
      <SwapInfo />
      <PInlineMessage v-if="ratesHasChanged" class="mt-3" severity="warn">
        {{ $t('errors.rateHasChanged') }}
      </PInlineMessage>
    </template>
  </SendTransaction>
</template>

<script lang="ts" setup>
import { computed, inject, ref, unref, watch } from 'vue';
import { SendTransaction } from '@/components/Dialogs/Steps';
import SwapPreview from './SwapPreview.vue';
import SwapInfo from './SwapInfo.vue';
import { AptosTxPayload } from '@/types/aptosResources';
import { getCurve } from '@/utils/contracts';
import { useSwapStore } from '@/store';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { useTimeoutPoll } from '@vueuse/core';
import { RECALCULATION_TIME } from '@/constants';
import { EMixpanelEventNames, EMixpanelProvideKeys } from '@/types/enums';
import { storeToRefs } from 'pinia';
import { useTokensStore } from '@/store';
import { useWalletPlugin } from '@/composables/useWalletPlugin';

const track: any = inject(EMixpanelProvideKeys.Track);

const emits = defineEmits(['success', 'reject', 'back', 'close']);

const swapStore = useSwapStore();
const { fromCurrency, toCurrency, lastInteractiveField } =
  storeToRefs(swapStore);

const view = ref<'root' | 'tx'>('root');
const isFrontrun = computed(() => swapStore.isFrontrunEnable);
const ratesHasChanged = ref(false);
const cachedPayload = ref<AptosTxPayload>();
const version = computed(() => swapStore.version);
const tokensStore = useTokensStore();
const { account } = useWalletPlugin();

const payload = computed<AptosTxPayload | undefined>(
  (): AptosTxPayload | undefined => {
    const { swapFunctionName, payload } = swapStore;

    const typeArguments = [
      ...payload.fullRoute,
      ...payload.curves,
      ...payload.binSteps,
    ];

    const isFromEmpty =
      !fromCurrency?.value.amount && lastInteractiveField.value === 'from';
    const isToEmpty =
      !toCurrency?.value.amount && lastInteractiveField.value === 'to';

    if (isFromEmpty || isToEmpty) return;

    const amount =
      swapStore.lastInteractiveField === 'from'
        ? (swapStore.fromCurrency.amount as number).toString()
        : swapStore.slippageAmount.toString();

    console.log(account.value);

    const args = [
      amount,
      [...payload.minimumExpectedValues],
      [...payload.versions],
      [...payload.swapsXForY],
    ];

    console.log('all', {
      function: swapFunctionName as `${string}::${string}::${string}`,
      typeArguments: typeArguments,
      functionArguments: args,
    });

    return {
      function: swapFunctionName as `${string}::${string}::${string}`,
      typeArguments: typeArguments,
      functionArguments: args,
    };
  },
);

watch(
  [payload, cachedPayload],
  () => {
    if (!payload.value || !cachedPayload.value) {
      ratesHasChanged.value = false;
      return;
    }

    ratesHasChanged.value = !isEqual(payload.value, cachedPayload.value);
  },
  { deep: true },
);

useTimeoutPoll(() => swapStore.refetchRates(true), RECALCULATION_TIME, {
  immediate: true,
});

const isCurveStable = computed(
  () => swapStore.curve === getCurve('stable', version.value),
);

function setView(value: 'root' | 'tx'): void {
  view.value = value;
}

function onSwapConfirm() {
  cachedPayload.value = cloneDeep(unref(payload.value));
  view.value = 'tx';
  track(EMixpanelEventNames.SwapConfirmClicked);
}

function onBack() {
  view.value = 'root';
  cachedPayload.value = undefined;
}

function onClose() {
  onBack();
  emits('close');
}

function onSuccess() {
  emits('success');
}

function onRejected() {
  emits('reject');
}
</script>
